import { HttpClient } from '@angular/common/http';
import {
  EnvironmentInjector,
  inject,
  Injectable,
  runInInjectionContext,
} from '@angular/core';
import { PanAppState } from '@panamax/app-state';
import { CoveoToken } from '../../models/coveo/coveo-token.model';
import { environment } from 'src/environments/environment';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  filter,
  firstValueFrom,
  map,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import { FEATURES } from '@shared/constants/splitio-features';
import { COVEO_TOKEN_LOCAL_STORAGE_KEY } from '@shared/constants/coveo-constants';
import { ToggleNewSearchService } from '../toggle-new-search/toggle-new-search.service';
import {
  buildCommerceEngine,
  CommerceEngine,
  ContextOptions,
  Context,
  buildContext,
  buildInstantProducts,
  InstantProducts,
  InstantProductsProps,
} from '@coveo/headless/commerce';
import { SessionState } from '@panamax/app-state/lib/models/session.model';

export type ClickValue = {
  productNumber: number;
  price: number;
};
@Injectable({
  providedIn: 'root',
})
export class CoveoService {
  private readonly environmentInjector = inject(EnvironmentInjector);
  private customContext: Record<string, string | number | boolean> = {};
  private context: Context;

  constructor(
    private panAppState: PanAppState,
    private toggleNewSearchService: ToggleNewSearchService,
    private http: HttpClient,
  ) {}

  coveoTokenSubscription$: Subscription;
  previousSession: SessionState;
  private coveoCommerceEngine: CommerceEngine;
  searchEngineSubject$: BehaviorSubject<CommerceEngine> = new BehaviorSubject(
    null,
  );
  private commerceEngineSubject$: BehaviorSubject<CommerceEngine> =
    new BehaviorSubject(null);
  private commerceEngine$: Observable<CommerceEngine> =
    this.commerceEngineSubject$.asObservable();
  private onClickEventSubject: Subject<ClickValue> = new Subject<ClickValue>();
  searchResultClickEvent$: Observable<ClickValue> =
    this.onClickEventSubject.asObservable();

  async getRefreshToken(): Promise<string> {
    try {
      const coveoResponse = await firstValueFrom(
        this.http.get<CoveoToken>(`${environment.coveoTokenUrl}`).pipe(
          map(coveoResponse => {
            localStorage.setItem(
              COVEO_TOKEN_LOCAL_STORAGE_KEY,
              coveoResponse.coveoToken,
            );
            return coveoResponse.coveoToken;
          }),
          catchError(err => {
            return of(null);
          }),
        ),
      );
      return coveoResponse;
    } catch (error) {
      throw error;
    }
  }

  getCoveoToken$() {
    return this.http.get<CoveoToken>(`${environment.coveoTokenUrl}`).pipe(
      map(coveoResponse => {
        localStorage.setItem(
          COVEO_TOKEN_LOCAL_STORAGE_KEY,
          coveoResponse.coveoToken,
        );
        this.initializeCommerceEngine(coveoResponse.coveoToken);
        return coveoResponse.coveoToken;
      }),
      catchError(err => of(err)),
    );
  }

  init() {
    this.coveoTokenSubscription$ = combineLatest([
      this.panAppState.session$,
      this.toggleNewSearchService.isNewSearchEnabled(),
      this.panAppState.feature$([
        FEATURES.split_global_search_new_search_analytics.name,
      ]),
    ])
      .pipe(
        filter(
          ([_, newSearchEnabled, newSearchAnalyticsFlag]) =>
            newSearchEnabled || newSearchAnalyticsFlag,
        ),
        concatMap(([session]) => {
          if (
            !this.previousSession ||
            session?.selectedCustomerId !==
              this.previousSession?.selectedCustomerId
          ) {
            this.previousSession = session;
            return this.getCoveoToken$();
          }

          if (localStorage.getItem(COVEO_TOKEN_LOCAL_STORAGE_KEY) === null) {
            return this.getCoveoToken$();
          } else {
            return of(null);
          }
        }),
      )
      .subscribe();
  }

  teardown() {
    this.coveoTokenSubscription$?.unsubscribe();
    localStorage.removeItem(COVEO_TOKEN_LOCAL_STORAGE_KEY);
  }

  initializeCommerceEngine(coveoToken: string) {
    this.coveoCommerceEngine = buildCommerceEngine({
      configuration: {
        accessToken: coveoToken,
        renewAccessToken: this.getRefreshToken.bind(this),
        organizationId: `${environment.coveoOrganizationId}`,
        analytics: {
          trackingId: 'us_foods',
          enabled: true,
        },
        context: {
          country: 'US',
          currency: 'USD',
          language: 'en',
          view: { url: `https://${environment.webDomain}/search2` },
        },
        preprocessRequest: (request, clientOrigin, metadata) => {
          // Check this is a Commerce API request.
          if (clientOrigin === 'commerceApiFetch') {
            const body: {
              context: ContextOptions;
              debug: boolean;
            } = JSON.parse(request.body as string);

            // Use inline injection to avoid circular dependency between services.
            runInInjectionContext(this.environmentInjector, () => {
              // Headless Commerce Context controller does not support setting
              // custom values, so this functionality has been implemented in
              // an Angular service and is injected below.
              body.context = {
                ...body.context,
                ...this.getCustomContext(),
              };
            });
            request.body = JSON.stringify(body);
          }
          return request;
        },
      },
    });
    this.commerceEngineSubject$.next(this.coveoCommerceEngine);
    this.context = buildContext(this.coveoCommerceEngine);
  }

  getSearchEngine$() {
    return this.searchEngineSubject$;
  }

  getCommerceEngine$() {
    return this.commerceEngine$;
  }

  onSearchResultClickEvent(productNumber: number, price: number = 0) {
    this.onClickEventSubject.next({ productNumber, price });
  }

  public getCustomContext(): Record<string, string | number | boolean> {
    return this.customContext;
  }

  public setCustomContext(key: string, value: string | number | boolean): void {
    this.customContext = {
      ...this.customContext,
      [key]: value,
    };
  }

  public deleteCustomContextKey(key: string): void {
    delete this.customContext[key];
  }

  public clearCustomContext(): void {
    this.customContext = {};
  }

  public async getContext(): Promise<Context> {
    if (this.context) {
      return this.context;
    }
    const engine = await firstValueFrom(
      this.commerceEngineSubject$.pipe(filter(value => !!value)),
    );
    this.context = buildContext(engine);
    return this.context;
  }

  buildInstantProducts(
    engine: CommerceEngine<{}>,
    props: InstantProductsProps,
  ): InstantProducts {
    return buildInstantProducts(engine, props);
  }
}
